import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { EventVenueMap } from 'src/components/Events/VenueMap/EventVenueMap';
import { CompListingsPreview } from 'src/components/Listings/CompListingsPreview';
import { CompListingsPreviewV2 } from 'src/components/Listings/CompListingsPreview/CompListingsPreviewV2';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import {
  BulkListingPriceCalculationResult,
  Event,
  Feature,
  Listing,
  ListingDetailsAutoPricingSectionUpdatesV2,
} from 'src/WebApiController';

import * as styles from './BulkEditAutoPricingSettingsSidePanel.css';
import {
  useBulkEditAutoPricingSettingsSidePanelEventMapProps,
  useBulkGetListingPriceCalculationsInEvent,
} from './BulkEditAutoPricingSettingsSidePanel.hooks';

export const BulkEditAutoPricingSettingsSidePanel = ({
  event,
  selectedListings,
  show,
  onPreviewCalculated,
}: {
  event: Event;
  selectedListings: Listing[];
  show?: boolean;
  onPreviewCalculated?: (
    result: BulkListingPriceCalculationResult | null
  ) => void;
}) => {
  const hasYourListingsInCompsFeature = useUserHasFeature(
    Feature.YourListingsInComps
  );
  const { bulkListingPriceCalculationResult, isLoading } =
    useBulkGetListingPriceCalculationsInEvent(event, selectedListings);
  const { eventsTransformed } = useCatalogDataContext();

  const flattenedEventListings = useMemo<Listing[]>(() => {
    return (
      (eventsTransformed
        ?.find((e) => e.event.virtId === event.virtId)
        ?.entities?.listings?.flatMap(flattenListingGroup) as Listing[]) ?? []
    );
  }, [event.virtId, eventsTransformed]);

  useEffect(() => {
    if (onPreviewCalculated) {
      onPreviewCalculated(bulkListingPriceCalculationResult ?? null);
    }
  }, [bulkListingPriceCalculationResult, onPreviewCalculated]);

  const eventVenueMapProps =
    useBulkEditAutoPricingSettingsSidePanelEventMapProps(
      event.viagId!,
      selectedListings,
      bulkListingPriceCalculationResult?.compListings
    );

  const { watch } = useFormContext<ListingDetailsAutoPricingSectionUpdatesV2>();
  const { compListingExcludeFanInventory } = watch();

  if (!show) {
    return null;
  }

  return (
    <Stack direction="column" className={styles.mainContainerFull}>
      <Stack gap={'xl'} className={styles.sectionContainer}>
        <div className={styles.mapContainerFull}>
          <EventVenueMap
            {...eventVenueMapProps}
            infoIconContent={<Content id={ContentId.AutoPricingDisclaimer} />}
          />
        </div>
        <Stack direction="column" className={styles.compsContainerFull}>
          <Stack className={styles.compsContainerHeader}>
            <FormatContent
              id={FormatContentId.MarketplaceListings}
              params={[selectedListings.length.toString()]}
            />
          </Stack>

          {hasYourListingsInCompsFeature ? (
            <CompListingsPreviewV2
              isLoading={isLoading}
              showListingsCarousel
              listings={flattenedEventListings}
              listingPriceCalculations={
                bulkListingPriceCalculationResult?.listingPriceCalculations ??
                []
              }
              compListings={
                bulkListingPriceCalculationResult?.compListings ?? []
              }
              isExcludingFanInventory={Boolean(
                compListingExcludeFanInventory?.value
              )}
              selectedListingIds={selectedListings?.map((l) => l.id) ?? []}
            />
          ) : (
            <CompListingsPreview
              isLoading={isLoading}
              isDetailPanel={false}
              listings={selectedListings}
              listingPriceCalculations={
                bulkListingPriceCalculationResult?.listingPriceCalculations ??
                []
              }
              compListings={
                bulkListingPriceCalculationResult?.compListings ?? []
              }
              sellerCompListings={
                bulkListingPriceCalculationResult?.sellerCompListings ?? []
              }
              isExcludingFanInventory={Boolean(
                compListingExcludeFanInventory?.value
              )}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
